import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { compoundSuffix, ISearchBody, ISearchSettings } from "@gtmhub/shared/services/searchService";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { IPromise, IQService } from "angular";
import { buildSearchConditions, responseFieldsMap, searchCollectionsMap, searchFieldsMap } from "../utils/global-search";
import { GlobalSearchRecentItemsFacetToggleOptionType } from "..";

export class GlobalSearchService {
  public static $inject = ["$rootScope", "FeatureToggleV2Service", "$q"];

  constructor(private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService, private featureToggleV2Service: FeatureToggleV2Service, private $q: IQService) {}

  private modulePermissionsMap = {
    sessions: (): IPromise<boolean> => {
      return this.$q.when(this.$rootScope.hasPermission("AccessGoals") && this.$rootScope.featureAvailable("okrs"));
    },
    okrs: (): IPromise<boolean> => {
      return this.$q.when(this.$rootScope.hasPermission("AccessGoals") && this.$rootScope.featureAvailable("okrs"));
    },
    tasks: (): IPromise<boolean> => {
      return this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ModuleTasks).then((isEnabled) => {
        return this.$rootScope.featureAvailable("hub.tasks") && isEnabled;
      });
    },
    lists: (): IPromise<boolean> => {
      return this.$q.when(true);
    },
    automations: (): IPromise<boolean> => {
      return this.$q.when(this.$rootScope.hasPermission("ManageData") && this.$rootScope.featureAvailable("automation"));
    },
    teams: (): IPromise<boolean> => {
      return this.$q.when(this.$rootScope.hasPermission("AccessPeople") && this.$rootScope.featureAvailable("people.teams"));
    },
    employees: (): IPromise<boolean> => {
      return this.$q.when(this.$rootScope.hasPermission("AccessPeople") && this.$rootScope.featureAvailable("people.employees"));
    },
    organization: (): IPromise<boolean> => {
      return this.$q.when(
        this.$rootScope.hasPermission("AccessPeople") && this.$rootScope.featureAvailable("people.teams") && this.$rootScope.featureAvailable("people.employees")
      );
    },
    kpis: (): IPromise<boolean> => {
      return this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ModuleKpis).then((isEnabled) => {
        return (
          this.$rootScope.hasPermission("AccessInsightboards") &&
          this.$rootScope.hasPermission("ManageData") &&
          this.$rootScope.featureAvailable("insightboards") &&
          this.$rootScope.featureAvailable("kpis") &&
          isEnabled
        );
      });
    },
    insightboards: (): IPromise<boolean> => {
      return this.$q.when(this.$rootScope.hasPermission("AccessInsightboards") && this.$rootScope.featureAvailable("insightboards"));
    },
    insights: (): IPromise<boolean> => {
      return this.$q.when(
        this.$rootScope.hasPermission("AccessInsightboards") && this.$rootScope.hasPermission("ManageData") && this.$rootScope.featureAvailable("insightboards")
      );
    },
    // eslint-disable-next-line sonarjs/no-identical-functions
    insightsAndInsightboards: (): IPromise<boolean> => {
      return this.$q.when(
        this.$rootScope.hasPermission("AccessInsightboards") && this.$rootScope.hasPermission("ManageData") && this.$rootScope.featureAvailable("insightboards")
      );
    },
    whiteboards: (): Promise<boolean> => {
      return this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ModuleWhiteboards).then((isEnabled) => {
        return isEnabled;
      });
    },
  };

  public getFacetToggleOptions(params: { isSearchV2: boolean }): IPromise<Record<string, string>> {
    return this.$q
      .all({
        isOkrsAvailable: this.modulePermissionsMap.okrs(),
        isSessionsAvailable: this.modulePermissionsMap.sessions(),
        isTasksAvailable: this.modulePermissionsMap.tasks(),
        isOrganizationAvailable: this.modulePermissionsMap.organization(),
        isEmployeeAvailable: this.modulePermissionsMap.employees(),
        isTeamAvailable: this.modulePermissionsMap.teams(),
        isListsAvailable: this.modulePermissionsMap.lists(),
        isInsightsAndInsightboardsAvailable: this.modulePermissionsMap.insightsAndInsightboards(),
        isInsightsAvailable: this.modulePermissionsMap.insights(),
        isInsightboardsAvailable: this.modulePermissionsMap.insightboards(),
        isKpisAvailable: this.modulePermissionsMap.kpis(),
        isAutomationsAvailable: this.modulePermissionsMap.automations(),
        isWhiteboardsAvailable: this.modulePermissionsMap.whiteboards(),
      })
      .then((response) => {
        return {
          everywhere: localize("everywhere"),
          ...(response.isOkrsAvailable && { okrs: localize("OKRs") }),
          ...(response.isSessionsAvailable && { sessions: localize("sessions") }),
          ...(response.isTasksAvailable && { tasks: localize("tasks_capital") }),
          ...(response.isOrganizationAvailable && { organization: params.isSearchV2 ? localize("employees") + " / " + localize("teams_cap") : localize("organization") }),
          ...(response.isEmployeeAvailable && { employees: localize("employees") }),
          ...(response.isTeamAvailable && { teams: localize("teams_cap") }),
          ...(response.isListsAvailable && { lists: localize("lists") }),
          ...(response.isInsightsAndInsightboardsAvailable && { insightsAndInsightboards: localize("insights_and_insightboards") }),
          ...(response.isInsightsAvailable && { insights: localize("insights") }),
          ...(response.isInsightboardsAvailable && { insightboards: localize("insightboards") }),
          ...(response.isKpisAvailable && { kpis: localize("kpis") }),
          ...(response.isAutomationsAvailable && { automations: localize("automations") }),
          ...(response.isWhiteboardsAvailable && { whiteboards: localize("whiteboards") }),
        };
      });
  }

  public getRecentItemsFacetToggleOptions(): IPromise<Record<GlobalSearchRecentItemsFacetToggleOptionType, string>> {
    return this.$q
      .all({
        isOkrsAvailable: this.modulePermissionsMap.okrs(),
        isSessionsAvailable: this.modulePermissionsMap.sessions(),
        isTasksAvailable: this.modulePermissionsMap.tasks(),
        isOrganizationAvailable: this.modulePermissionsMap.organization(),
        isEmployeeAvailable: this.modulePermissionsMap.employees(),
        isTeamAvailable: this.modulePermissionsMap.teams(),
        isListsAvailable: this.modulePermissionsMap.lists(),
        isInsightsAndInsightboardsAvailable: this.modulePermissionsMap.insightsAndInsightboards(),
        isInsightsAvailable: this.modulePermissionsMap.insights(),
        isInsightboardsAvailable: this.modulePermissionsMap.insightboards(),
        isKpisAvailable: this.modulePermissionsMap.kpis(),
        isAutomationsAvailable: this.modulePermissionsMap.automations(),
        isWhiteboardsAvailable: this.modulePermissionsMap.whiteboards(),
      })
      .then((response) => {
        return {
          everywhere: localize("everywhere"),
          ...(response.isOkrsAvailable && { okrs: localize("OKRs") }),
          ...(response.isSessionsAvailable && { sessions: localize("sessions") }),
          ...(response.isTasksAvailable && { tasks: localize("tasks_capital") }),
          ...(response.isOrganizationAvailable && { employeesAndTeams: localize("employees") + " / " + localize("teams_cap") }),
          ...(response.isEmployeeAvailable && { employees: localize("employees") }),
          ...(response.isTeamAvailable && { teams: localize("teams_cap") }),
          ...(response.isListsAvailable && { lists: localize("lists") }),
          ...(response.isInsightsAvailable && { insights: localize("insights") }),
          ...(response.isInsightboardsAvailable && { insightboards: localize("insightboards") }),
          ...(response.isKpisAvailable && { kpis: localize("kpis") }),
          ...(response.isAutomationsAvailable && { automations: localize("automations") }),
          ...(response.isWhiteboardsAvailable && { whiteboards: localize("whiteboards") }),
          otherItems: localize("other_items"),
        };
      });
  }

  public getSearchBody(searchTerm: string, currentFacet: string, settings: ISearchSettings = {}): ISearchBody {
    const compound = searchCollectionsMap[currentFacet].length > 1;

    return {
      searchTerm,
      operator: 0,
      searchRequests: searchCollectionsMap[currentFacet].map((collection: string) => {
        const collectionKey = compound ? collection + compoundSuffix : collection;

        return {
          collectionName: collection,
          searchFields: searchFieldsMap[collectionKey].map((field: string) => {
            return {
              name: field,
            };
          }),
          responseFields: responseFieldsMap[collectionKey],
          searchConditions: buildSearchConditions(collection, settings),
        };
      }),
    };
  }
}
